/**
 * decisionHumanizer — turn a raw OMEGA decision into one plain-English sentence
 * a non-technical founder can act on (WHAT it is + WHY).
 *
 * The backend gap-analyzer phrases a decision `question` from component scores,
 * e.g. "Gap: enterprise billing: low demand, moderate incumbents, weak GI fit —
 * opportunity score 42". That is gibberish to a human. This produces:
 *
 *   "Market opportunity: enterprise billing. Low demand, moderate competition.
 *    Confidence: High. Recommend: defer."
 *
 * When the raw text can't be parsed into the opportunity shape, it falls back to
 * a still-human sentence that never shows raw score noise:
 *
 *   "Decision: <question>. Confidence: <tier>. Recommended: <recommendation>."
 *
 * Shared by every decision render site (DecisionsTile, ActivityPage DecisionCard)
 * so the founder sees the same human text everywhere.
 *
 * Note on confidence: the backend carries confidence as a TIER (low/medium/high),
 * not a percentage, so the sentence shows the tier word (capitalized) rather than
 * a fabricated "X%".
 */

/** The decision fields this humanizer needs (subset of PendingDecision). */
export interface HumanizableDecision {
  question: string;
  recommendation: string;
  confidence: string;
}

/** Parsed market-opportunity components extracted from a gap `question`. */
interface ParsedOpportunity {
  /** Market segment / category, if the question names one. */
  category: string | null;
  /** Demand level word: "high" | "moderate" | "low". */
  demand: string;
  /** Competition level word: "strong" | "moderate" | "weak". */
  competition: string;
}

function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Parse the gap-analyzer reason out of a decision `question`. Returns `null`
 * unless BOTH a demand level and an incumbent (competition) level are present —
 * the caller then uses the plain fallback.
 */
export function parseOpportunity(question: string): ParsedOpportunity | null {
  const demandMatch = question.match(/(high|moderate|low)\s+demand/i);
  const incumbentMatch = question.match(/(strong|moderate|weak)\s+incumbents?/i);
  const demand = demandMatch?.[1]?.toLowerCase();
  // "strong incumbents" == strong competition; map the incumbent strength word
  // directly to a competition-strength word.
  const competition = incumbentMatch?.[1]?.toLowerCase();
  if (!demand || !competition) return null;

  return { category: extractCategory(question), demand, competition };
}

/**
 * Best-effort segment name: the text between a leading "Gap"/"Market gap" label
 * and the "… demand" reason. Returns `null` when the question goes straight to
 * the reason (no named segment), so the sentence omits the category cleanly.
 */
function extractCategory(question: string): string | null {
  // Drop the trailing "— opportunity score N" noise first.
  const stripped = question.replace(/[—-]\s*opportunity\s+score\s+\d+\s*$/i, '').trim();
  const m = stripped.match(
    /^(?:market\s+)?gap(?:\s+in)?\s*[:–-]?\s*(.+?)\s*[:–-]\s*(?:high|moderate|low)\s+demand/i,
  );
  const category = m?.[1]?.trim();
  return category && category.length > 0 ? category : null;
}

/**
 * Render a decision as one founder-readable sentence. Always states WHAT the
 * decision is and WHY, plus confidence + the recommended action.
 */
export function humanizeDecision(d: HumanizableDecision): string {
  const confidence = capitalize(d.confidence.trim() || 'unknown');
  const recommendation = d.recommendation.trim() || 'review';
  const parsed = parseOpportunity(d.question);

  if (parsed) {
    const lead = parsed.category
      ? `Market opportunity: ${parsed.category}.`
      : 'Market opportunity.';
    return (
      `${lead} ${capitalize(parsed.demand)} demand, ${parsed.competition} competition. ` +
      `Confidence: ${confidence}. Recommend: ${recommendation}.`
    );
  }

  const question = d.question.trim() || 'Pending decision';
  return `Decision: ${question}. Confidence: ${confidence}. Recommended: ${recommendation}.`;
}
